import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _, { isArray } from 'lodash';
import moment from 'moment';
import { AddIcon, EditIcon } from '../../../../components/Icon';
import SearchInput from '../../../../components/SearchInput';
import StyleButton from '../../../../components/StyleButton';
import TableUI from '../../../../components/TableUI';
import { i18n } from '../../../../language';
import { systemAPI } from '../../../../apis';
import { TAB_STATUS } from '../../../../comm/constant';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import AddHospitalTag from './Popup/AddHospitalTag';
import EditHospitalTag from './Popup/EditHospitalTag';

const useStyle = makeStyles((theme) => ({
  total: {
    fontSize: '1rem',
    color: '#7d86a9',
    marginBottom: '0.875rem',
  },
  tableContent: {
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.875rem',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '2.25rem',
    minHeight: '2.25rem',
    backgroundColor: '#538fff',
    borderRadius: '10px',
  },
  editIcon: {
    fontSize: '1rem',
    color: '#fff',
  },
}));

const HospitalTag = () => {
  const classes = useStyle();
  const [addTag, setAddTag] = useState(false);
  const [editTag, setEditTag] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [hospitalTagList, setHospitalTagList] = useState({
    count: 0,
    data: [],
  });
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize: 50,
    keyword: '',
    userId: loginInfo.userId,
  });
  const [refresh, setRefresh] = useState(false);

  const handleAddTag = useCallback(
    (open) => () => {
      setAddTag(open);
    },
    []
  );

  const handleEditTag = useCallback(
    (data) => () => {
      setEditTag(data);
    },
    []
  );

  const getHospitalTagList = useCallback(async () => {
    try {
      const { result } = await systemAPI.getHospitalTagList(pageInfo);
      if (pageInfo.pageNo > 1) {
        setHospitalTagList((oldData) => ({
          count: result.count,
          data: [...oldData.data, ...result.data],
        }));
      } else {
        setHospitalTagList(result);
      }
    } catch (error) {
      console.log(error);
    }
  }, [pageInfo]);

  useEffect(() => {
    (async () => {
      await getHospitalTagList();
    })();
  }, [getHospitalTagList]);

  useUpdateEffect(() => {
    setPageInfo((oldInfo) => ({ ...oldInfo, pageNo: 1 }));
  }, [refresh]);

  const tableData = useMemo(() => {
    if (isArray(hospitalTagList.data)) {
      return _.map(hospitalTagList.data, (item) => ({
        ...item,
        lastUpdate: item.lastUpdate ? moment(item.lastUpdate).format('yyyy-MM-DD HH:mm:ss') : '',
      }));
    }
    return [];
  }, [hospitalTagList.data]);

  const handleRefresh = useCallback(() => {
    setRefresh((o) => !o);
    setAddTag(false);
    setEditTag(false);
  }, []);

  const turnPage = useCallback(() => {
    if (hospitalTagList.data.length < hospitalTagList.count) {
      setPageInfo((oldInfo) => ({
        ...oldInfo,
        pageNo: oldInfo.pageNo + 1,
      }));
    }
  }, [hospitalTagList]);

  useEffect(() => {
    const pageRef = document.getElementById('tab-content');
    if (pageRef) {
      const handleScroll = (event) => {
        const { scrollTop, clientHeight, scrollHeight } = pageRef;

        if (scrollTop + clientHeight + 1 >= scrollHeight) {
          turnPage();
        }
      };

      pageRef.addEventListener('scroll', handleScroll);
      return () => pageRef.removeEventListener('scroll', handleScroll);
    }
  }, [turnPage]);

  const handleSearchInput = useCallback((value) => {
    setPageInfo((oldData) => ({
      ...oldData,
      keyword: value,
      pageNo: 1,
    }));
  }, []);

  return (
    <>
      <Box display="flex" p="1.875rem" borderBottom="solid 1px #ddd" justifyContent="space-between">
        <SearchInput
          width="13.188rem"
          height="2.563rem"
          placeholder={i18n.t('SETTING_SEARCH_TAG_PLACEHOLDER')}
          onChange={handleSearchInput}
        />
        <StyleButton
          type="fill"
          text={i18n.t('SETTING_ADD_TAG')}
          width="9.125rem"
          Icon={AddIcon}
          onClick={handleAddTag(true)}
        />
      </Box>
      <Box display="flex" flexDirection="column" p="1.25rem 1.875rem">
        <div className={classes.total}>{i18n.t('SETTING_RESULT', { length: tableData.length })}</div>
        <TableUI>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: '30%' }}>
                {i18n.t('SETTING_HOSPITAL_TAG_NAME')}
              </TableCell>
              <TableCell align="left" style={{ width: '30%' }}>
                {i18n.t('SETTING_HOSPITAL_STATUS')}
              </TableCell>
              <TableCell align="left" style={{ width: '20%' }}>
                {i18n.t('SETTING_HOSPITAL_LAST_UPDATE')}
              </TableCell>
              <TableCell align="left" style={{ width: '5%' }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((item) => (
              <TableRow onDoubleClick={handleEditTag(item)} key={item.id}>
                <TableCell>
                  <div className={classes.tableContent}> {item.tagDesc}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.tableContent}> {TAB_STATUS[item.status]}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.tableContent}> {item.lastUpdate}</div>
                </TableCell>
                <TableCell>
                  <ButtonBase className={classes.editBtn} onClick={handleEditTag(item)}>
                    <EditIcon className={classes.editIcon} />
                  </ButtonBase>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableUI>
      </Box>
      {addTag && <AddHospitalTag handleClose={handleAddTag(false)} handleRefresh={handleRefresh} />}
      {editTag ? (
        <EditHospitalTag handleClose={handleEditTag(false)} handleRefresh={handleRefresh} data={editTag} />
      ) : (
        ''
      )}
    </>
  );
};

export default HospitalTag;
